import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Accès aux fonctions IA selon le forfait
//
// Une fonctionnalité IA (OCR de facture, assistant, génération de contenu)
// n'est proposée qu'aux comptes dont la formule l'autorise.
//
// Ordre de vérification :
//  1. SuperAdmin / développeur → toujours autorisé.
//  2. `package_permissions` : autorisation explicite (permission = ia / ocr).
//  3. `package.features` / description : mention de l'IA.
//  4. Aucun abonnement → non autorisé.
// ─────────────────────────────────────────────────────────────

export interface AiAccess {
  allowed: boolean;
  planName: string | null;
  /** Explication courte affichée à l'utilisateur. */
  reason: string;
}

const AI_KEYWORDS = /\b(ia|ai|intelligence artificielle|assistant|ocr|grok|gpt|automatisation)\b/i;

interface SubscriptionRow {
  plan: string | null;
  status: string | null;
  end_date: string | null;
  created_at: string | null;
}

function isActiveSubscription(row: SubscriptionRow): boolean {
  const status = (row.status || '').toLowerCase();
  if (['cancelled', 'expired', 'refunded', 'failed'].includes(status)) return false;
  if (row.end_date) {
    const end = new Date(row.end_date);
    if (!Number.isNaN(end.getTime()) && end.getTime() < Date.now() - 86400000) return false;
  }
  return true;
}

/** Vérifie si le compte peut utiliser les fonctions IA. */
export async function resolveAiAccess(user: { id: number; typecompte?: number | null } | null): Promise<AiAccess> {
  if (!user?.id) {
    return { allowed: false, planName: null, reason: 'Connectez-vous pour utiliser les fonctions IA.' };
  }

  // Comptes plateforme (SuperAdmin / développeur) : accès total.
  const typecompte = Number(user.typecompte);
  if (typecompte === 1 || typecompte === 8 || typecompte === 9) {
    return { allowed: true, planName: 'Compte plateforme', reason: 'Accès plateforme complet.' };
  }

  try {
    const { data: subs } = await supabase
      .from('package_subscriptions')
      .select('plan, status, end_date, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    const rows = (subs || []) as SubscriptionRow[];
    const current = rows.find((r) => isActiveSubscription(r));
    if (!current?.plan) {
      return {
        allowed: false,
        planName: null,
        reason: 'Aucun forfait actif : les fonctions IA sont réservées aux forfaits compatibles.',
      };
    }

    const { data: pkg } = await supabase
      .from('package')
      .select('id, nom, description, features')
      .eq('nom', current.plan)
      .maybeSingle();

    const packId = Number((pkg as { id?: number } | null)?.id) || null;

    // 2. Autorisation explicite dans les permissions du forfait.
    if (packId) {
      const { data: perms } = await supabase
        .from('package_permissions')
        .select('permission, valeur')
        .eq('pack_id', packId);
      const allowedByPerm = ((perms || []) as { permission: string; valeur: number }[]).some(
        (p) => ['ia', 'ai', 'ocr', 'assistant_ia', 'assistant'].includes((p.permission || '').toLowerCase()) && Number(p.valeur) === 1,
      );
      if (allowedByPerm) {
        return { allowed: true, planName: current.plan, reason: 'Forfait compatible IA.' };
      }
    }

    // 3. Mention de l'IA dans les avantages du forfait.
    const features = (pkg as { features?: unknown; description?: string } | null)?.features;
    const featureText = Array.isArray(features) ? features.map((f) => String(f)).join(' ') : '';
    const description = (pkg as { description?: string } | null)?.description || '';
    if (AI_KEYWORDS.test(`${featureText} ${description}`)) {
      return { allowed: true, planName: current.plan, reason: 'Forfait compatible IA.' };
    }

    return {
      allowed: false,
      planName: current.plan,
      reason: `Votre forfait ${current.plan} n'inclut pas les fonctions IA. Passez à un forfait supérieur pour activer l'import automatique.`,
    };
  } catch {
    return {
      allowed: false,
      planName: null,
      reason: 'Impossible de vérifier votre forfait pour le moment.',
    };
  }
}